import { css } from "@emotion/css"
import { useCallback } from "react"

import NumberField from "@p-ban/common/src/components/form/NumberField/NumberField"
import Button from "@p-ban/common/src/components/html/Button"
import useDebounce from "@p-ban/common/src/hooks/useDebounce"

import useEstimateQuery from "../../../../../../api/estimate/useEstimateQuery"
import parseResult from "../../../../service/parseResult"
import type { EstimateParams } from "../../../../types/params"
import COLOR_CODE from "../../../constant/color"
import Cell from "./Cell"
import ColumnTemplate from "./ColumnTemplate"

type Props = {
  params: EstimateParams
  count: number | null
  setCount?: (value: number | null) => void
  onButtonClick?: () => void
  readOnly?: boolean
}

const Column = ({ params, count, setCount, onButtonClick, readOnly }: Props) => {
  const onUpdateValue = useCallback(
    (value: number | null) => {
      setCount?.(value)
    },
    [setCount]
  )
  const debounce = useDebounce(onUpdateValue)
  const { data, isLoading } = useEstimateQuery({ ...params, values: { ...params.values, cnt: count } })

  const Head = (
    <div className={styles.header}>
      <NumberField
        value={count}
        onUpdateValue={debounce}
        min={1}
        max={9999}
        maxDecimalDigits={0}
        step={1}
        suffix="枚"
        readOnly={readOnly}
        aria-label="製造枚数"
      />

      {!readOnly && (
        <div className={styles.buttonContainer}>
          <Button className={styles.button} hoverClassName={styles.buttonHover} onClick={onButtonClick}>
            選択
          </Button>
        </div>
      )}
    </div>
  )

  // 初回ローディング
  if (!data) {
    return <ColumnTemplate head={Head} isLoading={isLoading} />
  }

  if (data.isError) {
    return (
      <ColumnTemplate head={Head} isLoading={isLoading}>
        入力内容をご確認ください。
      </ColumnTemplate>
    )
  }

  const result = parseResult(data).manufacture

  if (!result) {
    return (
      <ColumnTemplate head={Head} isLoading={isLoading}>
        製造コースが選択されてません
      </ColumnTemplate>
    )
  }

  return (
    <ColumnTemplate head={Head} isLoading={isLoading} mapChildren>
      <Cell result={result.ultraQuick} />
      <Cell result={result.superQuick} />
      <Cell result={result.quick} />
      <Cell result={result.normal} />
      <Cell result={result.massProduction} />
    </ColumnTemplate>
  )
}

export default Column

const styles = {
  header: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    padding-top: 12px;
    padding-bottom: 10px;
    font-size: 13px;
    text-align: center;
    background-color: ${COLOR_CODE.SIMULATION_COURSE_GRAY};
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 10px;
  `,
  button: css`
    width: 80px;
    height: 24px;
    padding-bottom: 3px;
    font-size: 14px;
    line-height: 14px;
    color: ${COLOR_CODE.WHITE};
    background-color: ${COLOR_CODE.GREEN};
    border: none;
    border-radius: 5px;
  `,
  buttonHover: css`
    opacity: 0.7;
  `,
}
